import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { 
  UserIcon, 
  BellIcon, 
  CogIcon, 
  ShieldCheckIcon, 
  BuildingOfficeIcon,
  BanknotesIcon,
  Cog8ToothIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { companySettingsService } from '../services/system/company-settings';
import { userSettingsService } from '../services/system/users';
import LogoCropper from '../components/ui/LogoCropper';
import AccountMappingModal from '../components/accounting/AccountMappingModal';
import AuditTrailPanel from '../components/audit/AuditTrailPanel';

const Settings = () => {
  const { t } = useTranslation();
  const { user, userProfile, currentCompany, updateUserProfile, refreshCurrentCompany } = useAuth();
  const { isRTL } = useTheme();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [showAccountMappingModal, setShowAccountMappingModal] = useState(false);
  
  // User Settings State (commented out - will be implemented later)
  // const [userSettings, setUserSettings] = useState({
  //   emailNotifications: true,
  //   pushNotifications: true,
  //   smsNotifications: false,
  //   orderUpdates: true,
  //   marketingEmails: false,
  //   securityAlerts: true,
  //   language: 'en',
  //   timezone: 'Asia/Riyadh',
  //   dateFormat: 'DD/MM/YYYY',
  //   currency: 'SAR'
  // });

  // Profile State
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    department: '',
    bio: ''
  });

  // Company Settings State
  const [companyInfo, setCompanyInfo] = useState({
    companyName: '',
    companyNameEn: '',
    address: '',
    phone: '',
    website: '',
    email: '',
    vatId: '',
    iban: '',
    bankName: '',
    accountName: '',
    swiftCode: '',
    logoUrl: null,
    logoBase64: null,
  });

  // Payroll Automation Settings State
  const [payrollSettings, setPayrollSettings] = useState({
    defaultCreateJournalEntry: true,
    defaultCreateExpense: false,
    defaultSalaryAccount: '5100',
    defaultCommissionAccount: '5200',
    defaultCashAccount: '1000',
    defaultExpenseCategory: 'payroll'
  });

  // Logo Upload State

  const [showCropper, setShowCropper] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);

  // Password Change State (commented out - will be implemented later)
  // const [passwordData, setPasswordData] = useState({
  //   currentPassword: '',
  //   newPassword: '',
  //   confirmPassword: ''
  // });

  // Load user settings
  const loadUserSettings = useCallback(async () => {
    if (!user?.uid) return;
    try {
      // Load profile data
      if (userProfile) {
        setProfileData({
          name: userProfile.name || '',
          email: userProfile.email || '',
          phone: userProfile.phone || '',
          role: userProfile.role || '',
          department: userProfile.department || '',
          bio: userProfile.bio || ''
        });
      }
    } catch (error) {
      console.error('Error loading user settings:', error);
    }
  }, [user?.uid, userProfile]);

  // Load company settings
  const loadCompanySettings = useCallback(async () => {
    if (!currentCompany?.id) return;
    try {
      setLoading(true);
      const settings = await companySettingsService.getCompanySettings(currentCompany.id);
      if (settings) {
        setCompanyInfo({
          companyName: settings.companyName || '',
          companyNameEn: settings.companyNameEn || '',
          address: settings.address || '',
          phone: settings.phone || '',
          website: settings.website || '',
          email: settings.email || '',
          vatId: settings.vatId || '',
          iban: settings.iban || '',
          bankName: settings.bankName || '',
          accountName: settings.accountName || '',
          swiftCode: settings.swiftCode || '',
          logoUrl: settings.logoUrl || null,
          logoBase64: settings.logoBase64 || null,
        });

        // Load payroll settings
        setPayrollSettings({
          defaultCreateJournalEntry: settings.payrollSettings?.defaultCreateJournalEntry ?? true,
          defaultCreateExpense: settings.payrollSettings?.defaultCreateExpense ?? false,
          defaultSalaryAccount: settings.payrollSettings?.defaultSalaryAccount || '5100',
          defaultCommissionAccount: settings.payrollSettings?.defaultCommissionAccount || '5200',
          defaultCashAccount: settings.payrollSettings?.defaultCashAccount || '1000',
          defaultExpenseCategory: settings.payrollSettings?.defaultExpenseCategory || 'payroll'
        });
      }
    } catch (error) {
      console.error('Error loading company settings:', error);
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id]);

  // Removed unused saveUserSettings function - will be added when needed

  // Save profile
  const saveProfile = async () => {
    if (!user?.uid) return;
    try {
      setLoading(true);
      await userSettingsService.updateUserProfile(user.uid, profileData);
      if (updateUserProfile) {
        await updateUserProfile(profileData);
      }
      toast.success(t('settings.profile_updated', 'Profile updated successfully'));
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(t('settings.profile_update_failed', 'Failed to update profile'));
    } finally {
      setLoading(false);
    }
  };

  // Save company settings
  const saveCompanySettings = async () => {
    if (!currentCompany?.id) {
      toast.error(t('settings.no_company_selected', 'No company selected'));
      return;
    }
    
    try {
      setLoading(true);
      
      // Update company information (logo is handled separately now)
      console.log('Updating company settings:', companyInfo);
      await companySettingsService.updateCompanySettings(currentCompany.id, companyInfo);
      
      toast.success(t('settings.company_info_updated', 'Company information updated successfully'));
      
      // Reload to get updated data
      await loadCompanySettings();
      await refreshCurrentCompany();
    } catch (error) {
      console.error('Error saving company info:', error);
      toast.error(t('settings.company_info_update_failed', 'Failed to update company information: ') + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle logo upload
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(t('settings.logo_too_large', 'Logo file size must be less than 2MB'));
        return;
      }
      if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
        toast.error(t('settings.logo_invalid_format', 'Logo must be PNG or JPG format'));
        return;
      }
      
      const imageUrl = URL.createObjectURL(file);
      setSelectedImageUrl(imageUrl);
      setShowCropper(true);
    }
  };

  const handleCropComplete = async (croppedBlob) => {
    if (!currentCompany?.id) {
      console.error('No company ID found');
      toast.error(t('settings.no_company_selected', 'No company selected'));
      return;
    }

    try {
      setLoading(true);
      
      console.log('Converting logo to base64 data (no CORS issues)...');
      
      // Convert blob to base64 data URL
      const logoBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          console.log('✅ Logo converted to base64, size:', result.length);
          resolve(result);
        };
        reader.onerror = (error) => {
          console.error('❌ Error converting logo to base64:', error);
          reject(error);
        };
        reader.readAsDataURL(croppedBlob);
      });
      
      // Update company settings with base64 logo data
      const updatedCompanyInfo = { 
        ...companyInfo, 
        logoBase64: logoBase64,
        logoUrl: logoBase64 // Keep logoUrl for backward compatibility
      };
      
      console.log('Saving logo as base64 data to database...');
      await companySettingsService.updateCompanySettings(currentCompany.id, updatedCompanyInfo);
      console.log('✅ Logo base64 data saved successfully');
      
      // Update local state
      setCompanyInfo(updatedCompanyInfo);
      
      // Close cropper and clean up
      setShowCropper(false);
      if (selectedImageUrl) {
        URL.revokeObjectURL(selectedImageUrl);
        setSelectedImageUrl(null);
      }
      
      // Refresh company data to get the updated logo
      await Promise.all([
        loadCompanySettings(),
        refreshCurrentCompany()
      ]);
      
      toast.success(t('settings.logo_uploaded', 'Logo uploaded successfully! (No CORS issues)'));
      
    } catch (error) {
      console.error('Logo upload failed:', error.message);
      toast.error(t('settings.logo_upload_failed', 'Failed to upload logo: ') + error.message);
      
      // Still close cropper on error
      setShowCropper(false);
      if (selectedImageUrl) {
        URL.revokeObjectURL(selectedImageUrl);
        setSelectedImageUrl(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    if (selectedImageUrl) {
      URL.revokeObjectURL(selectedImageUrl);
      setSelectedImageUrl(null);
    }
  };

  // Removed unused changePassword function - will be added when needed

  const deleteLogo = async () => {
    if (!currentCompany?.id) {
      toast.error(t('settings.no_company_selected', 'No company selected'));
      return;
    }

    try {
      setLoading(true);
      
      // Update company settings to remove logo (both URL and base64 data)
      const updatedCompanyInfo = { 
        ...companyInfo, 
        logoUrl: null,
        logoBase64: null 
      };
      await companySettingsService.updateCompanySettings(currentCompany.id, updatedCompanyInfo);
      
      // Update local state
      setCompanyInfo(updatedCompanyInfo);
      
      // Refresh company data
      await loadCompanySettings();
      await refreshCurrentCompany();
      
      toast.success(t('settings.logo_deleted', 'Logo deleted successfully!'));
    } catch (error) {
      console.error('Error deleting logo:', error);
      toast.error(t('settings.logo_delete_failed', 'Failed to delete logo: ') + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Save payroll settings
  const savePayrollSettings = async () => {
    if (!currentCompany?.id) {
      toast.error(t('settings.no_company_selected', 'No company selected'));
      return;
    }
    
    try {
      setLoading(true);
      
      // Update company settings with payroll settings
      const updatedSettings = {
        ...companyInfo,
        payrollSettings
      };
      
      await companySettingsService.updateCompanySettings(currentCompany.id, updatedSettings);
      
      toast.success(t('settings.payroll_settings_updated', 'Payroll automation settings updated successfully'));
      
      // Reload to get updated data
      await loadCompanySettings();
    } catch (error) {
      console.error('Error saving payroll settings:', error);
      toast.error(t('settings.payroll_settings_update_failed', 'Failed to update payroll settings: ') + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserSettings();
    loadCompanySettings();
  }, [loadUserSettings, loadCompanySettings]);

  const tabs = [
    { id: 'profile', name: t('settings.profile', t('common.profile')), icon: UserIcon },
    { id: 'notifications', name: t('settings.notifications', 'Notifications'), icon: BellIcon },
    { id: 'preferences', name: t('settings.preferences', 'Preferences'), icon: CogIcon },
    { id: 'company', name: t('settings.company_info', 'Company Information'), icon: BuildingOfficeIcon },
    { id: 'accounting', name: t('settings.accounting', 'Accounting'), icon: BanknotesIcon },
    { id: 'payroll', name: t('settings.payroll_automation', 'Payroll Automation'), icon: UserIcon },
    { id: 'security', name: t('settings.security', 'Security'), icon: ShieldCheckIcon },
    { id: 'audit-trail', name: t('settings.audit_trail', 'Audit Trail'), icon: ClipboardDocumentListIcon }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {t('settings.title', t('common.settings'))}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('settings.subtitle', 'Manage your account settings and preferences')}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <nav className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <ul className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <li key={tab.id}>
                      <button
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          activeTab === tab.id
                            ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500'
                            : 'text-gray-700 hover:bg-gray-50'
                        } ${isRTL ? 'text-right' : 'text-left'}`}
                      >
                        <Icon className={`w-5 h-5 ${isRTL ? 'ml-3' : 'mr-3'} ${
                          activeTab === tab.id ? 'text-blue-500' : 'text-gray-400'
                        }`} />
                        {tab.name}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    {t('settings.profile_information', 'Profile Information')}
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.full_name', 'Full Name')}
                      </label>
                      <input
                        type="text"
                        value={profileData.name}
                        onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                        className="input-field w-full"
                        placeholder={t('settings.enter_name', 'Enter your full name')}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.email', t('common.email'))}
                      </label>
                      <input
                        type="email"
                        value={profileData.email}
                        disabled
                        className="input-field w-full bg-gray-50"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.phone', t('common.phone'))}
                      </label>
                      <input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                        className="input-field w-full"
                        placeholder="+966 50 123 4567"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.department', 'Department')}
                      </label>
                      <input
                        type="text"
                        value={profileData.department}
                        onChange={(e) => setProfileData(prev => ({ ...prev, department: e.target.value }))}
                        className="input-field w-full"
                        placeholder={t('settings.enter_department', 'Enter your department')}
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.bio', 'Bio')}
                      </label>
                      <textarea
                        value={profileData.bio}
                        onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                        rows={3}
                        className="input-field w-full"
                        placeholder={t('settings.enter_bio', 'Tell us about yourself')}
                      />
                    </div>
                  </div>
                  
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={saveProfile}
                      disabled={loading}
                      className="btn btn-primary"
                    >
                      {loading ? t('common.saving', 'Saving...') : t('common.save', 'Save Changes')}
                    </button>
                  </div>
                </div>
              )}

              {/* Company Tab */}
              {activeTab === 'company' && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    {t('settings.company_information', 'Company Information')}
                  </h2>
                  
                  {/* Logo Upload Section */}
                  <div className="mb-8 p-6 bg-gray-50 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {t('settings.company_logo', 'Company Logo')}
                    </h3>
                    <div className="flex items-center space-x-6">
                      {(companyInfo.logoBase64 || companyInfo.logoUrl) && (
                        <div className="relative">
                          <div className="w-24 h-24 rounded-lg overflow-hidden bg-white border-4 border-gray-200 shadow-sm">
                            <img 
                              src={companyInfo.logoBase64 || companyInfo.logoUrl} 
                              alt="Company Logo" 
                              className="w-full h-full object-cover"
                            />
                          </div>
                          {companyInfo.logoBase64 && (
                            <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                              ✓ No CORS
                            </div>
                          )}
                        </div>
                      )}
                      {!companyInfo.logoBase64 && !companyInfo.logoUrl && (
                        <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-100">
                          <span className="text-gray-400 text-xs text-center">
                            {t('settings.no_logo', 'No Logo')}
                          </span>
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="space-y-3">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg"
                            onChange={handleLogoUpload}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-3 file:px-6 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          />
                          {(companyInfo.logoBase64 || companyInfo.logoUrl) && (
                            <button
                              onClick={deleteLogo}
                              disabled={loading}
                              className="px-4 py-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                            >
                              {loading ? t('common.deleting', 'Deleting...') : t('settings.delete_logo', 'Delete Logo')}
                            </button>
                          )}

                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                          {t('settings.logo_upload_help', 'PNG or JPG up to 2MB. Logo will be saved as base64 data (no CORS issues).')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Company Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.company_name', 'Company Name')}
                      </label>
                      <input
                        type="text"
                        value={companyInfo.companyName}
                        onChange={(e) => setCompanyInfo(prev => ({ ...prev, companyName: e.target.value }))}
                        className="input-field w-full"
                        placeholder={t('settings.enter_company_name', 'Enter company name')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.company_name_en', 'Company Name in English')}
                      </label>
                      <input
                        type="text"
                        value={companyInfo.companyNameEn}
                        onChange={(e) => setCompanyInfo(prev => ({ ...prev, companyNameEn: e.target.value }))}
                        className="input-field w-full"
                        placeholder={t('settings.enter_company_name_en', 'Enter English company name')}
                        dir="ltr"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.phone', t('common.phone'))}
                      </label>
                      <input
                        type="tel"
                        value={companyInfo.phone}
                        onChange={(e) => setCompanyInfo(prev => ({ ...prev, phone: e.target.value }))}
                        className="input-field w-full"
                        placeholder="+966 11 123 4567"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.email', t('common.email'))}
                      </label>
                      <input
                        type="email"
                        value={companyInfo.email}
                        onChange={(e) => setCompanyInfo(prev => ({ ...prev, email: e.target.value }))}
                        className="input-field w-full"
                        placeholder="info@company.com"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.address', 'Address')}
                      </label>
                      <textarea
                        value={companyInfo.address}
                        onChange={(e) => setCompanyInfo(prev => ({ ...prev, address: e.target.value }))}
                        className="input-field w-full"
                        rows="3"
                        placeholder={t('settings.enter_address', 'Enter company address')}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.website', 'Website')}
                      </label>
                      <input
                        type="url"
                        value={companyInfo.website}
                        onChange={(e) => setCompanyInfo(prev => ({ ...prev, website: e.target.value }))}
                        className="input-field w-full"
                        placeholder="https://company.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.vat_number', 'VAT Number')}
                      </label>
                      <input
                        type="text"
                        value={companyInfo.vatId}
                        onChange={(e) => setCompanyInfo(prev => ({ ...prev, vatId: e.target.value }))}
                        className="input-field w-full"
                        placeholder="300000000000003"
                      />
                    </div>
                  </div>

                  {/* Financial Information */}
                  <div className="border-t pt-8">
                    <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                      <BanknotesIcon className="w-5 h-5 mr-2 text-green-600" />
                      {t('settings.financial_information', 'Financial Information')}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('settings.bank_name', 'Bank Name')}
                        </label>
                        <input
                          type="text"
                          value={companyInfo.bankName}
                          onChange={(e) => setCompanyInfo(prev => ({ ...prev, bankName: e.target.value }))}
                          className="input-field w-full"
                          placeholder={t('settings.enter_bank_name', 'Enter bank name')}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('settings.account_name', 'Account Name')}
                        </label>
                        <input
                          type="text"
                          value={companyInfo.accountName}
                          onChange={(e) => setCompanyInfo(prev => ({ ...prev, accountName: e.target.value }))}
                          className="input-field w-full"
                          placeholder={t('settings.enter_account_name', 'Enter account name')}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('chart.iban')}
                        </label>
                        <input
                          type="text"
                          value={companyInfo.iban}
                          onChange={(e) => setCompanyInfo(prev => ({ ...prev, iban: e.target.value }))}
                          className="input-field w-full"
                          placeholder="SA00 0000 0000 0000 0000 0000"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('settings.swift_code', 'SWIFT Code')}
                        </label>
                        <input
                          type="text"
                          value={companyInfo.swiftCode}
                          onChange={(e) => setCompanyInfo(prev => ({ ...prev, swiftCode: e.target.value }))}
                          className="input-field w-full"
                          placeholder="RIBLSARI"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 flex justify-end">
                    <button
                      onClick={saveCompanySettings}
                      disabled={loading}
                      className="btn btn-primary"
                    >
                      {loading ? t('common.saving', 'Saving...') : t('common.save', 'Save Changes')}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Payroll Automation Tab */}
              {activeTab === 'payroll' && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    {t('settings.payroll_automation', 'Payroll Automation Settings')}
                  </h2>
                  
                  <div className="space-y-6">
                    {/* Automation Status Overview */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-blue-900">
                          {t('payroll.automation.status', 'Payroll Automation Status')}
                        </h3>
                        <div className="flex items-center space-x-2">
                          <div className={`w-3 h-3 rounded-full ${(payrollSettings.defaultCreateJournalEntry || payrollSettings.defaultCreateExpense) ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                          <span className="text-sm font-medium text-blue-900">
                            {(payrollSettings.defaultCreateJournalEntry || payrollSettings.defaultCreateExpense) ? 
                              t('payroll.automation.enabled', 'Enabled') : 
                              t('payroll.automation.disabled', 'Disabled')
                            }
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg p-4 border border-blue-100">
                          <div className="flex items-center">
                            <div className={`w-2 h-2 rounded-full mr-2 ${payrollSettings.defaultCreateJournalEntry ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            <span className="text-sm font-medium text-gray-900">
                              {t('payroll.automation.journal_entries', 'Journal Entries')}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            {payrollSettings.defaultCreateJournalEntry ? 
                              t('payroll.automation.will_create', 'Will be created automatically') :
                              t('payroll.automation.will_not_create', 'Will not be created')
                            }
                          </p>
                        </div>
                        
                        <div className="bg-white rounded-lg p-4 border border-blue-100">
                          <div className="flex items-center">
                            <div className={`w-2 h-2 rounded-full mr-2 ${payrollSettings.defaultCreateExpense ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            <span className="text-sm font-medium text-gray-900">
                              {t('payroll.automation.expense_records', 'Expense Records')}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            {payrollSettings.defaultCreateExpense ? 
                              t('payroll.automation.will_create', 'Will be created automatically') :
                              t('payroll.automation.will_not_create', 'Will not be created')
                            }
                          </p>
                        </div>
                        
                        <div className="bg-white rounded-lg p-4 border border-blue-100">
                          <div className="flex items-center">
                            <div className="w-2 h-2 rounded-full mr-2 bg-blue-500"></div>
                            <span className="text-sm font-medium text-gray-900">
                              {t('payroll.automation.payroll_entries', 'Payroll Entries')}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            {t('payroll.automation.always_created', 'Always created')}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Automation Configuration */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        {t('payroll.automation.configuration', 'Automation Configuration')}
                      </h3>
                      <p className="text-sm text-gray-600 mb-6">
                        {t('payroll.automation.config_description', 'Configure what should be automatically created when processing payroll periods')}
                      </p>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Automation Options */}
                        <div>
                          <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                            <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mr-2">1</span>
                            {t('payroll.automation.choose_actions', 'Choose Actions')}
                          </h4>
                          <div className="space-y-4">
                            <div className="border border-gray-200 rounded-lg p-4">
                              <label className="flex items-start">
                                <input
                                  type="checkbox"
                                  checked={payrollSettings.defaultCreateJournalEntry}
                                  onChange={(e) => setPayrollSettings(prev => ({ ...prev, defaultCreateJournalEntry: e.target.checked }))}
                                  className="form-checkbox text-blue-600 mt-1"
                                />
                                <div className="ml-3">
                                  <span className="text-sm font-medium text-gray-900">
                                    {t('payroll.automation.create_journal_entry', 'Create Journal Entries')}
                                  </span>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {t('payroll.automation.journal_description', 'Creates accounting entries for salary and commission expenses')}
                                  </p>
                                </div>
                              </label>
                            </div>
                            
                            <div className="border border-gray-200 rounded-lg p-4">
                              <label className="flex items-start">
                                <input
                                  type="checkbox"
                                  checked={payrollSettings.defaultCreateExpense}
                                  onChange={(e) => setPayrollSettings(prev => ({ ...prev, defaultCreateExpense: e.target.checked }))}
                                  className="form-checkbox text-blue-600 mt-1"
                                />
                                <div className="ml-3">
                                  <span className="text-sm font-medium text-gray-900">
                                    {t('payroll.automation.create_expense', 'Create Expense Records')}
                                  </span>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {t('payroll.automation.expense_description', 'Creates individual expense records for each employee payment')}
                                  </p>
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Account Mapping */}
                        <div>
                          <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                            <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mr-2">2</span>
                            {t('payroll.automation.account_mapping', 'Account Mapping')}
                          </h4>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('payroll.automation.salary_account', 'Salary Expense Account')}
                              </label>
                              <input
                                type="text"
                                value={payrollSettings.defaultSalaryAccount}
                                onChange={(e) => setPayrollSettings(prev => ({ ...prev, defaultSalaryAccount: e.target.value }))}
                                className="form-input"
                                placeholder="5100"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                {t('payroll.automation.salary_account_desc', 'Account code for salary expenses')}
                              </p>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('payroll.automation.commission_account', 'Commission Expense Account')}
                              </label>
                              <input
                                type="text"
                                value={payrollSettings.defaultCommissionAccount}
                                onChange={(e) => setPayrollSettings(prev => ({ ...prev, defaultCommissionAccount: e.target.value }))}
                                className="form-input"
                                placeholder="5200"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                {t('payroll.automation.commission_account_desc', 'Account code for commission expenses')}
                              </p>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('payroll.automation.cash_account', 'Cash Account')}
                              </label>
                              <input
                                type="text"
                                value={payrollSettings.defaultCashAccount}
                                onChange={(e) => setPayrollSettings(prev => ({ ...prev, defaultCashAccount: e.target.value }))}
                                className="form-input"
                                placeholder="1000"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                {t('payroll.automation.cash_account_desc', 'Account code for cash payments')}
                              </p>
                            </div>
                            
                            {payrollSettings.defaultCreateExpense && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  {t('payroll.automation.expense_category', 'Expense Category')}
                                </label>
                                <select
                                  value={payrollSettings.defaultExpenseCategory}
                                  onChange={(e) => setPayrollSettings(prev => ({ ...prev, defaultExpenseCategory: e.target.value }))}
                                  className="form-select"
                                >
                                  <option value="payroll">{t('expenses.payroll', 'Payroll')}</option>
                                  <option value="salary">{t('expenses.salary', 'Salary')}</option>
                                  <option value="commission">{t('expenses.commission', 'Commission')}</option>
                                  <option value="other">{t('expenses.other', 'Other')}</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                  {t('payroll.automation.expense_category_desc', 'Category for expense records')}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Information Section */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-amber-900 mb-4 flex items-center">
                        <span className="w-5 h-5 text-amber-600 mr-2">ℹ️</span>
                        {t('settings.important_notes', 'Important Notes')}
                      </h3>
                      <div className="space-y-3 text-sm text-amber-800">
                        <div className="flex items-start">
                          <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <p>
                            <strong>{t('payroll.automation.centralized_settings', 'Centralized Settings')}:</strong> {t('payroll.automation.centralized_desc', 'These settings are applied automatically when processing payroll. No need to configure them each time.')}
                          </p>
                        </div>
                        <div className="flex items-start">
                          <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <p>
                            <strong>{t('payroll.automation.account_validation', 'Account Validation')}:</strong> {t('payroll.automation.account_validation_desc', 'Make sure the account codes exist in your Chart of Accounts before processing payroll.')}
                          </p>
                        </div>
                        <div className="flex items-start">
                          <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <p>
                            <strong>{t('payroll.automation.audit_trail', 'Audit Trail')}:</strong> {t('payroll.automation.audit_trail_desc', 'All automated entries include references to the original payroll period for easy tracking.')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 flex justify-end">
                    <button
                      onClick={savePayrollSettings}
                      disabled={loading}
                      className="btn btn-primary"
                    >
                      {loading ? t('common.saving', 'Saving...') : t('common.save', 'Save Settings')}
                    </button>
                  </div>
                </div>
              )}

              {/* Accounting Tab */}
              {activeTab === 'accounting' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {t('settings.accounting_settings', 'Accounting Settings')}
                    </h2>
                    <button
                      onClick={() => setShowAccountMappingModal(true)}
                      className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      title={t('settings.configure_accounting', 'Configure Accounting')}
                    >
                      <Cog8ToothIcon className="w-4 h-4" />
                      <span className="text-sm">{t('settings.configure_accounting', 'Configure Accounting')}</span>
                    </button>
                  </div>
                  
                  <div className="space-y-6">
                    {/* Account Mappings Section */}
                    <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-orange-900 mb-2 flex items-center">
                            <Cog8ToothIcon className="w-5 h-5 mr-2" />
                            {t('settings.account_mappings', 'Account Mappings')}
                          </h3>
                          <p className="text-orange-700 text-sm mb-4">
                            {t('settings.account_mappings_description', 'Configure which accounts are used for different types of transactions like revenue, expenses, and shipment costs. This ensures proper classification in your journal entries.')}
                          </p>
                          <div className="flex space-x-4 text-sm">
                            <div className="flex items-center text-green-600">
                              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                              {t('settings.revenue_accounts', 'Revenue Accounts')}
                            </div>
                            <div className="flex items-center text-red-600">
                              <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                              {t('settings.expense_accounts', 'Expense Accounts')}
                            </div>
                            <div className="flex items-center text-blue-600">
                              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                              {t('settings.asset_liability_accounts', 'Asset & Liability Accounts')}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowAccountMappingModal(true)}
                          className="ml-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2"
                        >
                          <Cog8ToothIcon className="w-4 h-4" />
                          <span>{t('settings.configure_mappings', 'Configure Mappings')}</span>
                        </button>
                      </div>
                    </div>

                    {/* Information Section */}
                                          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                        <h3 className="text-lg font-medium text-blue-900 mb-3">
                          {t('settings.automatic_entries', 'Automatic Journal Entries')}
                        </h3>
                        <div className="space-y-3 text-blue-800">
                          <div className="flex items-start space-x-3">
                            <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                            <div>
                              <p className="font-medium">{t('settings.shipment_invoicing', 'Shipment Invoicing')}</p>
                              <p className="text-sm text-blue-600">{t('settings.shipment_invoicing', 'When invoices are created from shipments, revenue journal entries are automatically created')}</p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-3">
                            <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                            <div>
                              <p className="font-medium">{t('settings.shipment_costing', 'Shipment Costing')}</p>
                              <p className="text-sm text-blue-600">{t('settings.shipment_costing', 'When shipment costs are entered, expense journal entries are automatically created')}</p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-3">
                            <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                            <div>
                              <p className="font-medium">{t('settings.expense_recording', 'Expense Recording')}</p>
                              <p className="text-sm text-blue-600">{t('settings.expense_recording', 'When a new expense is added, a journal entry is automatically created')}</p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-3">
                            <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                            <div>
                              <p className="font-medium">{t('settings.accounting_integration', 'Accounting Integration')}</p>
                              <p className="text-sm text-blue-600">{t('settings.accounting_integration', 'All transactions are integrated with the accounting system for accurate financial tracking')}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Auto-Invoice Settings */}
                      <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                        <h3 className="text-lg font-medium text-green-900 mb-3">
                          {t('settings.auto_invoice_settings', 'Automatic Invoice Creation')}
                        </h3>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-green-900">{t('settings.auto_invoice_on_delivery', 'Auto-create invoices on delivery')}</p>
                              <p className="text-sm text-green-600">{t('settings.auto_invoice_description', 'Automatically create and send invoices when shipments are marked as delivered')}</p>
                            </div>
                            <label className="flex items-center ml-4">
                              <input
                                type="checkbox"
                                checked={companyInfo.autoInvoiceOnDelivery || false}
                                onChange={(e) => setCompanyInfo(prev => ({ ...prev, autoInvoiceOnDelivery: e.target.checked }))}
                                className="sr-only"
                              />
                              <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                companyInfo.autoInvoiceOnDelivery ? 'bg-green-600' : 'bg-gray-200'
                              }`}>
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  companyInfo.autoInvoiceOnDelivery ? 'translate-x-6' : 'translate-x-1'
                                }`} />
                              </div>
                            </label>
                          </div>
                          {companyInfo.autoInvoiceOnDelivery && (
                            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                              <p className="text-sm text-yellow-800">
                                <strong>{t('settings.auto_invoice_note', 'Note:')}</strong> {t('settings.auto_invoice_warning', 'When enabled, invoices will be automatically created and marked as "sent" when shipments are delivered. This will also trigger automatic revenue recognition in journal entries.')}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                  </div>
                </div>
              )}

              {activeTab === 'audit-trail' && <AuditTrailPanel />}

            </div>
          </div>
        </div>

        {/* Logo Cropper Modal */}
        {showCropper && selectedImageUrl && (
          <LogoCropper
            image={selectedImageUrl}
            onCropComplete={handleCropComplete}
            onCancel={handleCropCancel}
            loading={loading}
          />
        )}

        {/* Account Mapping Modal */}
        <AccountMappingModal
          isOpen={showAccountMappingModal}
          onClose={() => setShowAccountMappingModal(false)}
        />
      </div>
    </div>
  );
};

export default Settings;
